/**
 * Measured connection health — the input to the degradation ladder.
 *
 * Two signals are combined and the WORSE always wins: LiveKit's own
 * `ConnectionQualityChanged` (which knows about RTT and the publisher side) and
 * our measured downstream loss from `getStats()`. A room is never reported
 * healthier than the packets say it is.
 */
import type { Room } from 'livekit-client';
import type { ConnectionQuality } from './contract';
import { readingFrom, sampleInbound, worseQuality, ZERO_SAMPLE, type StatsSample } from './livekitStats';
import type { MediaStore } from './mediaStore';

/** Study §7 degrades one rung at 5 % downstream loss. */
const LOSS_POOR_THRESHOLD = 0.05;

export interface QualityTracker {
  /** One stats sample: publishes downlink, loss and the settled quality. */
  tick(room: Room): Promise<void>;
  /** LiveKit's signal for the local participant. */
  setSignalled(quality: ConnectionQuality): void;
}

export function createQualityTracker(store: MediaStore): QualityTracker {
  let previous: StatsSample = ZERO_SAMPLE;
  let signalled: ConnectionQuality = 'excellent';

  const settle = (loss: number): void => {
    const measured: ConnectionQuality = loss > LOSS_POOR_THRESHOLD ? 'poor' : 'excellent';
    const quality = worseQuality(signalled, measured);
    if (quality !== store.get().quality) store.patch({ quality });
  };

  return {
    async tick(room) {
      const next = await sampleInbound(room);
      const reading = readingFrom(previous, next);
      previous = next;
      store.patch({ downlinkBps: reading.downlinkBps, packetLoss: reading.packetLoss });
      settle(reading.packetLoss);
    },

    setSignalled(quality) {
      signalled = quality;
      settle(store.get().packetLoss);
    },
  };
}
